import { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Button, Chip, Grid, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { AppContext } from "../../App";
import BaseDialog from "../../components/BaseDialog";
import Panel from "../../components/Panel";
import DataLabel from "../../components/DataLabel";
import ComboSelect from "../../components/ComboSelect";
import NumberField from "../../components/NumberField";
import CheckBox from "../../components/CheckBox";
import LoadingDialog from "../../components/LoadingDialog";
import { calculateValueFromEvent, toComboValues } from "../../components/Utils";
import { hasErrors, required, validate, validateAll } from "../../components/Validators";
import { formatDate } from "../../components/Formatter";
import useApi from "../../api";
import { actualizarNodo, consultarNodo } from "../../api/nodos";
import { SIZE_NODO } from "./ListadoNodos";

const fieldsValidators = {
    'size': [required],
    'espacioDisponible': [required],
}

const siNo = (value) => value === true ? 'Sí' : value === false ? 'No' : null;

const formatGiB = (mib) => mib ? `${(mib / 1024).toLocaleString('es-MX', { maximumFractionDigits: 1 })} GiB` : null;

// Memoria del host repartida entre las instancias que ya tiene y los espacios que le quedan: es lo
// que tendría cada instancia si el nodo se llenara
export const calculaMemoriaPorInstancia = (memoriaMiB, instancias, espacioDisponible) => {
    const ocupados = (instancias || []).filter(i => i.estatus !== 'ELIMINADA').length;
    const lugares = ocupados + (Number(espacioDisponible) || 0);
    if (!memoriaMiB || lugares === 0) {
        return null;
    }
    return { memoriaMiB: memoriaMiB / lugares, lugares };
}

const Item = ({ value, label, md = 4 }) => (
    <Grid item xs={12} sm={6} md={md}>
        <DataLabel value={value} label={label} />
    </Grid>
);

const LoadingSkeleton = () => (
    <Grid container spacing={2}>
        {Array.from(new Array(12)).map((item, index) => (
            <Grid key={index} item xs={12} sm={6} md={4}>
                <Skeleton variant="rounded" animation="wave" height={25} width="60%" />
                <Skeleton variant="rounded" animation="wave" height={25} sx={{ mt: 1 }} />
            </Grid>
        ))}
    </Grid>
);

const DetalleNodoDialog = ({ ...props }) => {
    const { nodoId } = useParams();
    const { state } = useLocation();
    const navigate = useNavigate();
    const { notificar, tieneCuentaPermisos } = useContext(AppContext);
    const puedeAdministrar = tieneCuentaPermisos(['PERM_ADMIN_CLIENTES']);

    const [open, setOpen] = useState(true);
    const [detalle, setDetalle] = useState(null);
    const [data, setData] = useState(null);
    const [errors, setErrors] = useState({ size: '', espacioDisponible: '' });

    const [{ data: detalleData, loading: loadingDetalle }, detalleApi] = useApi();
    const [{ data: actualizarData, loading: loadingActualizar }, actualizarApi] = useApi();

    useEffect(() => {
        detalleApi(consultarNodo(nodoId));
    }, [nodoId]);

    useEffect(() => {
        if (detalleData) {
            setDetalle(detalleData);
            setData({
                size: detalleData.nodo.size || '',
                espacioDisponible: detalleData.nodo.espacioDisponible || 0,
                reservado: detalleData.nodo.reservado || false,
            });
        }
    }, [detalleData]);

    useEffect(() => {
        if (actualizarData) {
            notificar("Nodo actualizado correctamente");
            if (props.onShouldUpdate) {
                props.onShouldUpdate();
            }
            detalleApi(consultarNodo(nodoId));
        }
    }, [actualizarData]);

    const nodo = detalle ? detalle.nodo : null;
    const cloud = detalle ? detalle.cloudHost : null;
    const instancias = nodo && nodo.instancias ? nodo.instancias : [];

    const huboCambios = nodo && data && (data.size !== (nodo.size || '')
        || Number(data.espacioDisponible) !== (nodo.espacioDisponible || 0)
        || data.reservado !== (nodo.reservado || false));
    const esValido = data && !hasErrors(validateAll(data, fieldsValidators));

    const handleChangeValue = name => event => {
        const value = calculateValueFromEvent(event);
        setData({ ...data, [name]: value });
        setErrors({ ...errors, [name]: validate(value, fieldsValidators[name]) });
    }

    const handleGuardar = () => {
        actualizarApi(actualizarNodo(nodoId, { ...data, espacioDisponible: Number(data.espacioDisponible) }));
    }

    const handleExited = () => {
        navigate("..", { state: { ...state } });
    };

    const handleClose = () => {
        setOpen(false);
    };

    const memoria = cloud ? calculaMemoriaPorInstancia(cloud.memoriaMiB, instancias, data ? data.espacioDisponible : 0) : null;

    return (
        <>
            <BaseDialog open={open} onExited={handleExited} onClose={handleClose} fullWidth maxWidth="lg"
                title={nodo ? `Nodo ${nodo.cloudInstanceId || nodo.id}` : 'Nodo'}
                actions={
                    <>
                        <Button onClick={handleClose} color="primary">Cerrar</Button>
                        {puedeAdministrar &&
                            <Button onClick={handleGuardar} color="primary" variant="contained" disabled={!huboCambios || !esValido || loadingActualizar}>Guardar</Button>
                        }
                    </>
                }>
                {loadingDetalle && !detalle && <LoadingSkeleton />}
                {nodo &&
                    <Box>
                        {cloud && cloud.advertencias && cloud.advertencias.map((advertencia, index) =>
                            <Alert key={index} severity="warning" sx={{ mb: 2 }}>{advertencia}</Alert>
                        )}

                        <Panel title="Nodo" gutterBottom>
                            <Grid container spacing={2}>
                                <Item value={nodo.cloudInstanceId} label="Id en la nube" />
                                <Item value={nodo.servicio} label="Servicio" />
                                <Item value={nodo.estatus} label="Estatus" />
                                <Item value={nodo.ipAddress} label="IP pública" />
                                <Item value={nodo.privateIpAddress} label="IP privada" />
                                <Item value={formatDate(nodo.finCambioEstatus)} label="Último cambio" />
                                <Item value={`${instancias.filter(i => i.estatus !== 'ELIMINADA').length}`} label="Instancias hospedadas" />
                                <Item value={detalle.capacidad} label="Capacidad según su tamaño" />
                                <Item value={nodo.reservado ? 'Sí' : 'No'} label="Reservado" />
                            </Grid>
                        </Panel>

                        {puedeAdministrar && data &&
                            <Panel title="Espacios y asignación" gutterBottom>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <ComboSelect
                                            fullWidth
                                            required
                                            field="size"
                                            label="Tamaño del nodo"
                                            value={data.size}
                                            onChange={handleChangeValue('size')}
                                            errors={errors}
                                            options={toComboValues(SIZE_NODO)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <NumberField
                                            fullWidth
                                            required
                                            field="espacioDisponible"
                                            label="Espacios disponibles"
                                            helperText="Cuántas instancias más se le pueden asignar; 0 para ninguna"
                                            value={data.espacioDisponible}
                                            onChange={handleChangeValue('espacioDisponible')}
                                            onBlur={() => { }}
                                            errors={errors}
                                            formatter={{ decimalScale: 0, allowNegative: false }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <CheckBox
                                            label="Reservado (excluir de la asignación automática)"
                                            field="reservado"
                                            checked={data.reservado}
                                            onChange={handleChangeValue('reservado')}
                                        />
                                    </Grid>
                                </Grid>
                            </Panel>
                        }

                        {cloud &&
                            <Panel title="Tipo de instancia en AWS" gutterBottom>
                                <Grid container spacing={2}>
                                    <Item value={cloud.tipo} label="Tipo" />
                                    <Item value={cloud.vcpus ? `${cloud.vcpus} (${cloud.nucleos} núcleos × ${cloud.hilosPorNucleo} hilos)` : null} label="vCPU" />
                                    <Item value={formatGiB(cloud.memoriaMiB)} label="Memoria" />
                                    <Item value={memoria ? `${formatGiB(memoria.memoriaMiB)} (entre ${memoria.lugares} lugares)` : null} label="Memoria por instancia con el nodo lleno" />
                                    <Item value={cloud.arquitectura} label="Arquitectura" />
                                    <Item value={cloud.velocidadGhz ? `${cloud.velocidadGhz} GHz` : null} label="Velocidad sostenida" />
                                    <Item value={cloud.rendimientoRed} label="Rendimiento de red" />
                                    <Item value={siNo(cloud.rendimientoRafaga)} label="Rendimiento en ráfaga (créditos de CPU)" />
                                    <Item value={siNo(cloud.generacionActual)} label="Generación actual" />
                                </Grid>
                            </Panel>
                        }

                        {cloud && cloud.estado &&
                            <Panel title="Host en AWS" gutterBottom>
                                <Grid container spacing={2}>
                                    <Item value={cloud.nombre} label="Nombre" />
                                    <Item value={cloud.motivoEstado ? `${cloud.estado} — ${cloud.motivoEstado}` : cloud.estado} label="Estado" />
                                    <Item value={cloud.verificacionSistema} label="Verificación del sistema" />
                                    <Item value={cloud.verificacionInstancia} label="Verificación de la instancia" />
                                    <Item value={formatDate(cloud.lanzamiento)} label="Lanzamiento" />
                                    <Item value={cloud.zonaDisponibilidad} label="Zona de disponibilidad" />
                                    <Item value={cloud.plataforma} label="Plataforma" />
                                    <Item value={cloud.amiId} label="AMI" />
                                    <Item value={cloud.keyName} label="Key pair" />
                                    <Item value={cloud.vpcId} label="VPC" />
                                    <Item value={cloud.subnetId} label="Subred" />
                                    <Item value={cloud.ipPublica} label="IP pública" />
                                    <Item value={cloud.dnsPublico} label="DNS público" md={8} />
                                    <Item value={cloud.dnsPrivado} label="DNS privado" md={8} />
                                    <Item value={cloud.monitoreo} label="Monitoreo" />
                                    <Item value={siNo(cloud.ebsOptimizada)} label="EBS optimizada" />
                                    <Item value={cloud.dispositivoRaiz} label="Dispositivo raíz" />
                                    <Item value={cloud.perfilIam} label="Perfil IAM" md={8} />
                                    <Item value={cloud.gruposSeguridad ? cloud.gruposSeguridad.join(', ') : null} label="Grupos de seguridad" md={12} />
                                    {cloud.eventosProgramados && cloud.eventosProgramados.length > 0 &&
                                        <Item value={cloud.eventosProgramados.join('; ')} label="Eventos programados" md={12} />
                                    }
                                </Grid>
                            </Panel>
                        }

                        {cloud && cloud.volumenes && cloud.volumenes.length > 0 &&
                            <Panel title="Volúmenes" gutterBottom>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Dispositivo</TableCell>
                                            <TableCell>Id</TableCell>
                                            <TableCell align="right">Capacidad</TableCell>
                                            <TableCell>Tipo</TableCell>
                                            <TableCell align="right">IOPS</TableCell>
                                            <TableCell>Estado</TableCell>
                                            <TableCell>Cifrado</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {cloud.volumenes.map(v =>
                                            <TableRow key={v.volumeId}>
                                                <TableCell>{v.dispositivo}</TableCell>
                                                <TableCell>{v.volumeId}</TableCell>
                                                <TableCell align="right">{v.capacidadGiB ? `${v.capacidadGiB} GiB` : ''}</TableCell>
                                                <TableCell>{v.tipo}</TableCell>
                                                <TableCell align="right">{v.iops}</TableCell>
                                                <TableCell>{v.estado}</TableCell>
                                                <TableCell>{siNo(v.cifrado)}</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Panel>
                        }

                        {cloud && cloud.etiquetas && Object.keys(cloud.etiquetas).length > 0 &&
                            <Panel title="Etiquetas" gutterBottom>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {Object.entries(cloud.etiquetas).map(([key, value]) =>
                                        <Chip key={key} size="small" variant="outlined" label={`${key}: ${value}`} />
                                    )}
                                </Box>
                            </Panel>
                        }

                        <Panel title="Instancias en el nodo">
                            {instancias.length === 0 &&
                                <Typography variant="body2" color="text.secondary">El nodo no hospeda instancias</Typography>
                            }
                            {instancias.length > 0 &&
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Instancia</TableCell>
                                            <TableCell>Cliente</TableCell>
                                            <TableCell>Estatus</TableCell>
                                            <TableCell>Activa</TableCell>
                                            <TableCell>Último cambio</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {instancias.map(i =>
                                            <TableRow key={i.id}>
                                                <TableCell>{i.cliente ? i.cliente.siteName : i.id}</TableCell>
                                                <TableCell>
                                                    {i.cliente ? <Link to={`/clientes/${i.cliente.id}`}>{i.cliente.nombre}</Link> : ''}
                                                </TableCell>
                                                <TableCell>{i.estatus}</TableCell>
                                                <TableCell>{i.activa ? 'Sí' : 'No'}</TableCell>
                                                <TableCell>{formatDate(i.finCambioEstatus)}</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            }
                        </Panel>
                    </Box>
                }
            </BaseDialog>
            <LoadingDialog open={loadingActualizar} />
        </>
    );
};

export default DetalleNodoDialog;
